<template>
  <BaseModal modal-content-class="other_modals add_icon">
    <div class="content">
      <button @click="install()">
        <BaseImage src="/static/images/other/add_icon.svg" alt="add icon" />
      </button>

      <div class="ios" v-if="isIOSMobileDevice">
        <p>{{ $_translations.value.home_shortcut_ios_text1 }}</p>

          <ol>
            <li>{{ $_translations.value.home_shortcut_ios_text2 }}</li>
            <li>{{ $_translations.value.home_shortcut_ios_text3_1 }} <b>{{ $_translations.value.home_shortcut_ios_text3_2 }}</b></li>
            <li>{{ $_translations.value.home_shortcut_ios_text4 }}</li>
          </ol>

      </div>
      <div class="no_ios" v-else>
        <p>{{ $_translations.value.home_shortcut_android_text1 }}</p>
        <p>{{ $_translations.value.home_shortcut_android_text2 }}</p>
      </div>

      <BaseCheckboxFormInput
        v-model="doNotShowAgain"
        :label="$_translations.value.home_shortcut_input_label_remember_me"
        container-class="remember_checkbox dont_show"
      />
    </div>
  </BaseModal>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import BaseModal from '@/components/modal/BaseModal.vue';
import BaseImage from '@/components/image/BaseImage.vue';
import BaseCheckboxFormInput from '@/components/formInput/BaseCheckboxFormInput.vue';
import usePhoneShortcutStore from '@/store/modules/phoneShortcut.js';
import { phoneShortcutInstalled } from '@/services/phoneShortcut.js';
import useDeviceHook from '@/hooks/useDevice.js';
import $ from 'jquery';

// Emits
const emit = defineEmits(['close', 'ok']);

// Store
const phoneShortcutStore = usePhoneShortcutStore();

// Hook
const { isIOSMobileDevice } = useDeviceHook();

// Reactive data
const doNotShowAgain = ref(false);
const deferredPrompt = ref(null);
const promptEventReceived = ref(false);


// Methods
const beforeInstallPromptHandler = (event) => {

  event.preventDefault();
  deferredPrompt.value = event;
  promptEventReceived.value = true;
  phoneShortcutStore.setInstallPromptEvent(event);
};

const installiOSdevice = async () => {
  try {
    console.log('📱 iOS installation process');
    await phoneShortcutInstalled({ shortcutCreated: true });
    if (doNotShowAgain.value) {
      localStorage.setItem('homeScreenModalApplied', 'true');
    }
    emit('ok');
  } catch (error) {
    console.error('❌ Failed to record iOS shortcut installation:', error);
    if (doNotShowAgain.value) {
      localStorage.setItem('homeScreenModalApplied', 'true');
    }
    emit('ok');
  }
};

const close = () => {
  console.log('❌ Modal closed by user');
  if (doNotShowAgain.value) {
    localStorage.setItem('homeScreenModalDenied', 'true');
    phoneShortcutStore.storeHomeScreenModalDisable(true);
  } else {
    sessionStorage.setItem('homeScreenModalDenied', 'true');
  }
  emit('close');
};

const closeModal = () => {
  emit('ok');
};

const installWithPrompt = async () => {
  console.log('🚀 Installing with native prompt...');
  const prompt = deferredPrompt.value || phoneShortcutStore.getInstallPromptEvent();

  if (prompt) {
    try {
      await prompt.prompt();
      const choiceResult = await prompt.userChoice;
      console.log('User choice:', choiceResult);

      if (choiceResult.outcome === 'accepted') {
        console.log('✅ User accepted the install prompt');
        try {
          await phoneShortcutInstalled({ shortcutCreated: true });
          localStorage.setItem('homeScreenModalApplied', 'true');
        } catch (error) {
          console.error('Failed to record installation:', error);
          localStorage.setItem('homeScreenModalApplied', 'true');
        }
      } else {
        if (doNotShowAgain.value) {
          localStorage.setItem('homeScreenModalDenied', 'true');
        } else {
          sessionStorage.setItem('homeScreenModalDenied', 'true');
        }
      }

      deferredPrompt.value = null;
      phoneShortcutStore.clearInstallPromptEvent();
      closeModal();

    } catch (error) {
      console.error('❌ Error during installation:', error);
      closeModal();
    }
  } else {
    console.warn('⚠️ No install prompt available');
    closeModal();
  }
};

const install = () => {
  console.log('Install button clicked');
  if (isIOSMobileDevice) {
    installiOSdevice();
  } else {
    installWithPrompt();
  }
};

const handleBeforeInstallPrompt = (e) => {
  beforeInstallPromptHandler(e);
};

const handleAppInstalled = (e) => {
  localStorage.setItem('homeScreenModalApplied', 'true');
  deferredPrompt.value = null;
  phoneShortcutStore.clearInstallPromptEvent();
};

const registerCloseListeners = () => {
  $('.modal-overlay, .close-btn').click(close);
  $('.modal-content').click(event => {
    event.stopPropagation();
  });
};

// Lifecycle
onMounted(async () => {
  registerCloseListeners();
  window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
  window.addEventListener('appinstalled', handleAppInstalled);

  const existingPrompt = phoneShortcutStore.getInstallPromptEvent();
  if (existingPrompt) {
    deferredPrompt.value = existingPrompt;
    promptEventReceived.value = true;
  }

});

onUnmounted(() => {
  window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
  window.removeEventListener('appinstalled', handleAppInstalled);
});

// Expose methods
defineExpose({
  close
});
</script>
